/* eslint-disable functional/prefer-readonly-type */

import React, { useEffect } from 'react'
import ReactDOM from 'react-dom'
import StandardModal from '../../../../components/StandardModal'
import { useLang } from '../../../../hooks/useLang'
import useSystemState from '~/features/System/stores/SystemState'
import { storeScreenSettings } from '~/features/System/services'
import { Typography } from '@mui/material'
import { useHookstate } from '@hookstate/core'
import type ScreenSettingsDraggableProps from '~/features/System/interfaces/ScreenSettings'
import { authFetch } from '~/services/fetch'
import ResultSearchWordCloud from './components/ResultSearchWordCloud'
import useWordCloudState from '../../stores/WordCloudState'
import { InsertDriveFile } from '@mui/icons-material'
import { truncateText } from '~/utils/strings'

// eslint-disable-next-line max-lines-per-function
export default function SearchWordCloudModalDraggable() {
  const { translate } = useLang()
  const { windowManager, loadingButton } = useSystemState()
  const { wordCloud, totalOccurrences, totalOccurrencesLastHour, getOccurrenceWord } =
    useWordCloudState()
  const modal = document.getElementById('div-modal')
  const loadingComponent = useHookstate(true)
  const reloadList = useHookstate(false)
  const getPositionsDraggable = useHookstate<ScreenSettingsDraggableProps>({
    id: 0,
    user_id: 0,
    url: '',
    window_name: '',
    x_position: 80,
    y_position: 141,
    is_window_open: true,
    is_window_minimized: false,
    created_at: new Date().toJSON(),
    updated_at: new Date().toJSON(),
    deleted_at: null,
  })

  useEffect(() => {
    authFetch({
      url: '/screen-setting/get-user-settings',
      method: 'POST',
      data: {
        url: location.pathname,
      }
    }).then((response) => {
      if (
        response?.data &&
        typeof response.data === 'object' &&
        'SearchWordCloudModalDraggable' in response.data
      ) {
        getPositionsDraggable.set(response.data.SearchWordCloudModalDraggable)
      }
      loadingComponent.set(false)
    })

    return () => {
      windowManager()['searchWordCloud'].open.set(false)
    }
  }, [])

  const getOccurrencesWordFetch = async () => {
    const selectedWord = wordCloud().get()

    if (!selectedWord) {
      return
    }

    await getOccurrenceWord({ word: selectedWord }).catch((error_) => {
      console.error('Error fetching occurrences for word cloud:', error_)
    })
  }

  useEffect(() => {
    getOccurrencesWordFetch()
      .finally(() => {
        reloadList.set(!reloadList.get())
      })
  }, [wordCloud().value])

  const handleStop = (event, dragElement) => {
    event.stopPropagation()
    event.preventDefault()

    getPositionsDraggable.x_position.set(dragElement.x)
    getPositionsDraggable.y_position.set(dragElement.y)
  }


  useEffect(() => {
    return () => {
      storeScreenSettings({
        windowName: 'SearchWordCloudModalDraggable',
        isOpen: windowManager()['searchWordCloud'].open.get(),
        isMinimized: getPositionsDraggable.is_window_minimized?.get(),
        positionX: getPositionsDraggable?.x_position?.get(),
        positionY: getPositionsDraggable?.y_position?.get(),
      })
    }
  }, [
    windowManager()['searchWordCloud'].open.get(),
    getPositionsDraggable.is_window_minimized?.get(),
    getPositionsDraggable?.x_position?.get(),
    getPositionsDraggable?.y_position?.get(),
  ])

  if (!modal) {
    return null
  }

  if (windowManager()['searchWordCloud'].open.get()) {
    return ReactDOM.createPortal(
      // eslint-disable-next-line react/jsx-no-useless-fragment
      <>
        {!loadingButton.get() && !loadingComponent.get() && (
          <StandardModal
            avatar={InsertDriveFile}
            title={
              <Typography variant='subtitle1'>
                {translate('Occurrences')}
              </Typography>
            }
            subheader={
              <>
                <Typography variant="subtitle2">
                  {`${translate('Last occurrences hours', { hours: 24 })}`}
                </Typography>
                <Typography variant="subtitle2">
                  {`${translate('Word')}: ${truncateText(wordCloud().get(), 25)}`}
                </Typography>
                <Typography variant="subtitle2">
                  {`${translate('total_occurrences')}: ${totalOccurrences().get()}`}
                </Typography>
                <Typography variant="subtitle2">
                  {`${translate('Total occurrences last hour')}: ${totalOccurrencesLastHour().get()}`}
                </Typography>
              </>
            }
            dimensions={{
              width: 400,
            }}
            sx={{ maxWidth: '400px' }}
            handleStop={handleStop}
            position={{
              x: getPositionsDraggable?.x_position?.get() ? Number(getPositionsDraggable?.x_position?.get()) : 80,
              y: getPositionsDraggable?.y_position?.get() ? Number(getPositionsDraggable?.y_position?.get()) : 141,
            }}
            closeButton={() => windowManager()['searchWordCloud'].open.set(false)}
            isExpanded={getPositionsDraggable.is_window_minimized}
          >
            <ResultSearchWordCloud reloadList={reloadList} />
          </StandardModal>
        )}
      </>,
      modal
    )
  } else {
    return null
  }
}